import { css } from '@emotion/react';
import { Fragment } from 'react';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';

const containerDisabledStyle = css({
  opacity: .5,
});

const optionStyle = css({
  display: 'inline-block',
  padding: 4,
  borderRadius: 4,
  border: 'none',
  background: 'none',
  color: 'inherit',
});

const selectableLabelStyle = css({
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const unselectableLabelStyle = (theme: Theme) => css({
  background: theme.tagColors.secondaryBackgroundColor,
  color: theme.tagColors.secondaryTextColor,
});

const disabledLabelStyle = css({
  cursor: 'not-allowed',
  '&:hover': {
    textDecoration: 'none',
  },
});

const dividerStyle = css({
  margin: '0 10px',
});

type SwitchOption<T> = {
  value: T;
  label: string;
  tooltip?: string;
};

type SwitchOptionsProps<T> = Readonly<{
  containerStyle?: string;
  value: T;
  options: ReadonlyArray<SwitchOption<T>>;
  onChange: (selected: T) => void;
  isDisabled?: boolean;
  className?: string;
}>;

export const SwitchOptionsComponent = <T, >({
  containerStyle,
  value,
  className,
  options,
  isDisabled = false,
  onChange,
}: SwitchOptionsProps<T>) => {
  return (
    <div
      className={className}
      css={[containerStyle, isDisabled ? containerDisabledStyle : null]}
    >
      {options.map((option, index) => (
        <Fragment key={index}>
          <TooltipComponent
            placement={TooltipPlacement.Top}
            tooltipContent={option.tooltip}
          >
            <button
              onClick={isDisabled ? undefined : () => onChange(option.value)}
              css={[optionStyle, value === option.value ? unselectableLabelStyle : selectableLabelStyle, isDisabled ? disabledLabelStyle : null]}
            >
              {option.label}
            </button>
          </TooltipComponent>
          {index < options.length - 1 && (
            <span css={dividerStyle}>/</span>
          )}
        </Fragment>
      ))}
    </div>
  );
};
